import { useState, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { LABEL, CUSTOM_MESSAGES } from '@veriables';
import { transferCheckToTable } from '@reducers/storeChecks';
import ModalHeader from '@components/common/ModalHeader';
import { TouchableButton } from '@components/common/Touchable';
import { ReactComponent as Cross } from '@assets/images/cross.svg';
import { APP_CONFIG } from '@constants';
import { flowType } from '@utility/userInfo';

const FullWidthModal = lazy(() => import('@components/common/Modal/FullWidthModal'));
const CustomKeyboard = lazy(() => import('@components/common/CustomKeyboardCopy/indexRevamp'));

const useTableTransfer = ({ checkData, authInfo, serverAppTheme, setErrorPopUp, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [tableValue, setTableValue] = useState('');
  const [filteredValue, setFilteredValue] = useState([]);
  const [selectedTable, setSelectedTable] = useState([]);
  const [typed, setTyped] = useState('');

  const isTab = checkData?.tab;
  const stations = authInfo?.stations || [];

  const typedRef = useRef(typed);
  typedRef.current = typed;

  const open = useCallback(() => setIsOpen(true), []);

  const reset = useCallback(() => {
    setIsOpen(false);
    setTableValue('');
    setFilteredValue([]);
    setSelectedTable([]);
    setTyped('');
  }, []);

  const filteringData = useCallback(
    value => {
      if (isTab) return;
      const filtered =
        value ? stations.filter(s => s?.name?.startsWith(value)) : [];
      setFilteredValue(filtered);
    },
    [isTab, stations]
  );

  const onClickBtn = useCallback(
    e => {
      const newVal =
        e === '{bksp}' ? typedRef.current?.slice(0, -1) : typedRef.current + e;
      setTyped(newVal);
      setTableValue(newVal);
      filteringData(newVal);
    },
    [filteringData]
  );

  const validateTable = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (!tableValue && !selectedTable[0]?.number) {
          setErrorPopUp({ res: CUSTOM_MESSAGES().ENTER_TABLE_NUMBER, noErrorTxt: true });
          reject();
          return;
        }
        if (
          !stations.some(e => e.number === selectedTable[0]?.number) &&
          !stations.some(e => Number(e.number) === Number(tableValue))
        ) {
          setErrorPopUp({ res: 'Table number does not exist', noErrorTxt: true });
          reject();
          return;
        }
        resolve();
      }),
    [tableValue, selectedTable, stations, setErrorPopUp]
  );

  const handleTableTransfer = useCallback(() => {
    validateTable()
      .then(() => {
        const station = stations.find(
          item =>
            item?.number === tableValue ||
            item?.number === selectedTable[0]?.number
        );
        dispatch(
          transferCheckToTable({
            storeId: authInfo?.id,
            checkHeaderId: checkData?.id,
            stationId: station?.id || tableValue,
          })
        )
          .then(() => {
            onClose();
            reset();
            navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, {
              replace: true,
            });
          })
          .catch(err => setErrorPopUp({ res: err?.message }));
      })
      .catch(() => {});
  }, [validateTable, stations, tableValue, selectedTable, dispatch, authInfo, checkData, onClose, reset, setErrorPopUp]);

  const handleTabTransfer = useCallback(() => {
    if (!tableValue) {
      setErrorPopUp({ res: 'Enter Tab Name', noErrorTxt: true });
      return;
    }
    dispatch(
      transferCheckToTable({
        storeId: authInfo?.id,
        name: tableValue,
        checkHeaderId: checkData?.id,
        stationId: null,
      })
    )
      .then(() => {
        onClose();
        reset();
        navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, {
          replace: true,
        });
      })
      .catch(err => setErrorPopUp({ res: err?.message }));
  }, [tableValue, dispatch, authInfo, checkData, onClose, reset, setErrorPopUp, navigate]);

  const modalStyle = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      pointerEvents: 'auto',
      backgroundColor: serverAppTheme?.popUp_bg,
      backgroundClip: 'padding-box',
      height: '100%',
      borderRadius: '16px',
      border: serverAppTheme?.popupBorder,
      boxShadow: serverAppTheme?.popUp_box_shadow,
    }),
    [serverAppTheme]
  );

  const jsx = isOpen ? (
    <Suspense fallback={null}>
      <FullWidthModal
        isOpen={isOpen}
        customClass2="w-100 h-100"
        noBackground
        style={modalStyle}
      >
        <div
          className="col-12 px-0 m-auto h-100 justify-content-center align-items-center position-relative text-center"
          style={{ color: serverAppTheme?.primary_text }}
        >
          <ModalHeader
            isTab={isTab}
            stationName={checkData?.stationName}
            tileHeader={`Transfer ${isTab ? 'Tab' : 'Table'}`}
            seatNumber={checkData?.number}
          />
          <div className="mt-3 px-2">
            <div className="col-12 px-0">
              <div className="col-12 px-0 text-center mb-3 fw-600 fs_16">
                {isTab
                  ? CUSTOM_MESSAGES().CHK_INSTRUCTION_TRANSFER_TAB
                  : CUSTOM_MESSAGES().CHK_INSTRUCTION_TRANSFER_TABLE}
              </div>
              <div className="row mx-0">
                <div
                  className={cn(
                    'col-12 px-0 position-relative d-flex justify-content-center align-items-center',
                    filteredValue?.length > 0 && 'border-bottom'
                  )}
                >
                  <input
                    value={
                      selectedTable?.length > 0
                        ? selectedTable[0]?.name || selectedTable[0]?.number
                        : tableValue
                    }
                    id="tableTransfer"
                    name="tableTransfer"
                    type="text"
                    placeholder={
                      isTab
                        ? CUSTOM_MESSAGES().ENTER_TAB_NAME
                        : CUSTOM_MESSAGES().ENTER_TABLE_NUMBER
                    }
                    className="col-12 py-2 fs_16 pl-5"
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: serverAppTheme?.inputBg,
                      borderBottom: serverAppTheme?.inputBorder_Bottom,
                      color: serverAppTheme?.primary_text,
                      '--placeholder-color': serverAppTheme?.input_placeholder,
                    }}
                    onFocus={() => setSelectedTable([])}
                    readOnly
                  />
                  {tableValue?.length > 0 && (
                    <button
                      type="button"
                      className="position-absolute border-0 bg-transparent p-0"
                      style={{ right: '18px', top: '4px' }}
                      onClick={() => {
                        setTableValue('');
                        setTyped('');
                        setFilteredValue([]);
                      }}
                    >
                      <Cross alt="cross" fill={serverAppTheme?.iconcolor} />
                    </button>
                  )}
                </div>
              </div>
            {selectedTable?.length > 0 && filteredValue?.length > 0 && ( <ul
                className="col-12 px-0 list-unstyled overflowY roundedSm"
                style={{
                  border: serverAppTheme?.popupBorder,
                  maxHeight: '43vh',
                }}
              >
                {filteredValue.map(val => (
                  <li
                    key={val.id || val.number}
                    className="col-12 px-5 text-left py-1 d-flex align-items-center"
                    style={{
                      backgroundColor:
                        selectedTable[0]?.number === val.number &&
                        serverAppTheme?.drop_down_selected,
                      color: serverAppTheme?.primary_text,
                    }}
                    role="button"
                    tabIndex="0"
                    onClick={() => {
                      setSelectedTable([val]);
                      setFilteredValue([]);
                    }}
                  >
                    {val.name || val.number}
                  </li>
                ))}
              </ul>
              )}
            </div>

            <div
              className="col-12 row mx-0 justify-content-center mt-3 position-absolute"
              style={{ bottom: '16px', left: 0 }}
            >
              <div className="col-4 px-0 pr-1 d-flex justify-content-start align-items-center">
                <TouchableButton
                  type="button"
                  className="col-12 px-0 rounded btn-ripple fs_24 fw-600 btn44"
                  onClick={reset}
                  style={{
                    backgroundColor: serverAppTheme?.OFF_Premise_Btn,
                    border: serverAppTheme?.cancleBtn_Border,
                    color: serverAppTheme?.cancleBtn_font,
                  }}
                  rippleEffect
                >
                  {LABEL().CANCEL}
                </TouchableButton>
              </div>
              <div className="col-4 px-0 pl-1 d-flex justify-content-end align-items-center">
                <TouchableButton
                  type="button"
                  className="col-12 px-0 rounded btn-ripple fs_24 fw-600 btn44"
                  onClick={() => (isTab ? handleTabTransfer() : handleTableTransfer())}
                  style={{
                    backgroundColor: serverAppTheme?.secondaryBtn,
                    color: serverAppTheme?.btn_font_color,
                  }}
                  rippleEffect
                >
                  {LABEL().TRANSFER}
                </TouchableButton>
              </div>
            </div>
          </div>

          <Suspense fallback={null}>
            <div
              className="w-100 position-fixed"
              style={{ zIndex: 10000, bottom: '16%', left: 0 }}
            >
              <CustomKeyboard
                onClickBtn={onClickBtn}
                type={isTab ? 'textDefaultCaps' : 'numericAlpha'}
                closeKeyboard={() => {}}
                btnSize="btn35"
                isDone={false}
                customClass="h-auto mt-auto p-2"
                callApi
                isOrderSeat
              />
            </div>
          </Suspense>
        </div>
      </FullWidthModal>
    </Suspense>
  ) : null;

  return { open, jsx };
};

export default useTableTransfer;
